import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CTAButton } from '../ui/CTAButton';
import { BookOpen, Trophy, ArrowRight } from 'lucide-react';
type PlanId = 'standard' | 'pack';
const plans = [{
  id: 'standard' as PlanId,
  icon: BookOpen,
  name: 'DE PAREJA A FAMILIA',
  meta: '3 tomos + 3 bonos estratégicos',
  oldPrice: '$34.990',
  price: '$19.990',
  bullets: ['Tomo 1: Nosotros antes del bebé', 'Tomo 2: El equipo que somos', 'Tomo 3: Llegando juntos a él', '3 Bonos Estratégicos'],
  cta: '💛 QUIERO EL STANDARD',
  link: 'https://checkout.ideahubdigital.online/cart/48886322102491:1',
  featured: false
}, {
  id: 'pack' as PlanId,
  icon: Trophy,
  name: 'PACK COMPLETO',
  meta: '3 tomos + 3 bonos + 2 Tomos de Posparto',
  oldPrice: '$43.990',
  price: '$29.990',
  bullets: ['Todo lo del Standard', 'Tomo 4: Posparto (90 días)', 'Tomo 5: Equipo a largo plazo'],
  cta: 'SÍ, QUIERO EL PACK COMPLETO',
  link: 'https://checkout.ideahubdigital.online/cart/48886322233563:1',
  featured: true
}];
export function Pricing() {
  const [selected, setSelected] = useState<PlanId>('pack');
  const activePlan = plans.find(p => p.id === selected)!;
  return <section id="pricing" className="py-8 md:py-12 px-4 md:px-8 max-w-3xl mx-auto" data-id="element-207">
      <div className="text-center mb-6 md:mb-8" data-id="element-208">
        <h2 className="font-serif text-2xl md:text-4xl font-bold text-brown mb-2 md:mb-3" data-id="element-209">
          Elegí su <span className="text-terracotta" data-id="element-210">De Pareja a Familia</span>
        </h2>
        <p className="font-sans text-sm md:text-base text-brown/70" data-id="element-211">
          Ambas opciones incluyen los 3 bonos estratégicos completos
        </p>
      </div>

      <div role="radiogroup" aria-label="Planes" className="space-y-3 md:space-y-4" data-id="element-212">
        {plans.map(plan => {
        const isSelected = selected === plan.id;
        return <motion.button key={plan.id} type="button" role="radio" aria-checked={isSelected} onClick={() => setSelected(plan.id)} whileTap={{
          scale: 0.99
        }} className={`w-full text-left rounded-2xl border-2 p-4 md:p-5 transition-colors ${isSelected ? 'border-terracotta bg-terracotta/5' : 'border-brown/15 bg-offwhite hover:border-terracotta/40'}`} data-id="element-213">
              <div className="flex items-start gap-3 md:gap-4" data-id="element-214">
                {/* Radio */}
                <span className={`mt-0.5 w-5 h-5 md:w-6 md:h-6 rounded-full border-2 shrink-0 flex items-center justify-center transition-colors ${isSelected ? 'border-terracotta' : 'border-brown/30'}`} data-id="element-215">
                  {isSelected && <span className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full bg-terracotta" data-id="element-216" />}
                </span>

                {/* Content */}
                <div className="flex-1 min-w-0" data-id="element-217">
                  <div className="flex items-start justify-between gap-3" data-id="element-218">
                    <div className="min-w-0" data-id="element-219">
                      <div className="flex items-center gap-2 mb-1 flex-wrap" data-id="element-220">
                        <plan.icon size={18} className="text-terracotta shrink-0" data-id="element-221" />
                        <h3 className="font-serif font-bold text-base md:text-lg text-brown" data-id="element-222">
                          {plan.name}
                        </h3>
                      </div>

                      {plan.featured && <div className="flex items-center gap-2 mb-1.5 flex-wrap" data-id="element-223">
                          <span className="bg-terracotta text-offwhite font-sans font-bold text-[10px] md:text-xs px-2 py-0.5 rounded" data-id="element-224">
                            ⭐ MÁS ELEGIDO
                          </span>
                          <span className="bg-sage/20 text-sage font-sans font-bold text-[10px] md:text-xs px-2 py-0.5 rounded" data-id="element-225">
                            AHORRÁS $14.000
                          </span>
                        </div>}

                      <p className="font-sans text-xs md:text-sm text-brown/60" data-id="element-226">
                        {plan.meta}
                      </p>
                    </div>

                    {/* Price */}
                    <div className="text-right shrink-0" data-id="element-227">
                      <span className="block line-through text-brown/40 font-sans text-xs md:text-sm" data-id="element-228">
                        {plan.oldPrice}
                      </span>
                      <span className="font-serif text-2xl md:text-3xl font-bold text-terracotta leading-none" data-id="element-229">
                        {plan.price}
                      </span>
                    </div>
                  </div>

                  {/* Bullets */}
                  <ul className="mt-3 flex flex-wrap gap-x-3 gap-y-1" data-id="element-230">
                    {plan.bullets.map((b, i) => <li key={i} className="font-sans text-[11px] md:text-xs text-brown/70 flex items-center gap-1" data-id="element-231">
                        <span className="w-1 h-1 rounded-full bg-terracotta" data-id="element-232" />
                        {b}
                      </li>)}
                  </ul>
                </div>
              </div>
            </motion.button>;
      })}
      </div>

      {/* Single CTA reflecting selection */}
      <div className="mt-5 md:mt-6" data-id="element-233">
        <CTAButton variant="primary" fullWidth className="!py-5 text-base md:text-lg" onClick={() => window.location.href = activePlan.link} data-id="element-234">
          {activePlan.cta}
          <ArrowRight size={20} className="ml-2" data-id="element-235" />
        </CTAButton>
        <p className="text-center font-sans text-[11px] md:text-xs text-brown/50 mt-3 italic" data-id="element-236">
          La opción más elegida por parejas que quieren llegar preparadas
          también al después
        </p>
      </div>

      {/* Trust row */}
      <div className="flex flex-wrap justify-center items-center gap-3 md:gap-6 mt-6 md:mt-8 text-xs md:text-sm font-sans text-brown/60" data-id="element-237">
        <div className="flex items-center gap-1.5 md:gap-2" data-id="element-238">Pago seguro</div>
        <div className="w-1 h-1 bg-brown/30 rounded-full" data-id="element-239"></div>
        <div className="flex items-center gap-1.5 md:gap-2" data-id="element-240">
          Acceso en 5 min
        </div>
        <div className="w-1 h-1 bg-brown/30 rounded-full" data-id="element-241"></div>
        <div className="flex items-center gap-1.5 md:gap-2" data-id="element-242">
          Garantía 7 días
        </div>
        <div className="w-1 h-1 bg-brown/30 rounded-full" data-id="element-243"></div>
        <div className="flex items-center gap-1.5 md:gap-2 font-bold" data-id="element-244">
          Mercado Pago
        </div>
      </div>
    </section>;
}